import { create } from 'zustand';
import { enqueueSnackbar } from 'notistack';
import { t } from 'i18next';

import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import { UploadedFile } from 'src/interfaces/FileManager';
import { SECURE_API } from 'src/api/api';

// 2. Define the state interface
interface FileManagerState {
  files: UploadedFile[];
  currentPage: number;
  totalPages: number;
  totalFiles: number;
  loading: boolean;
  error: string | null;

  // Filters
  search: string;
  endpoint: 'all' | 'image' | 'document' | 'trackMap';
  isUsedFilter: 'all' | 'true' | 'false'; // String to match query param
  sortBy: 'createdAt' | 'fileName' | 'fileSize';
  sortOrder: 'asc' | 'desc';
  limit: number;

  // Pending files for deferred usage tracking
  pendingFiles: Map<string, string[]>; // key: "contentType:contentId:fieldName", value: fileIds[]
}

// 3. Define the actions interface
interface FileManagerActions {
  setSearch: (search: string) => void;
  setEndpointFilter: (endpoint: FileManagerState['endpoint']) => void;
  setIsUsedFilter: (isUsedFilter: FileManagerState['isUsedFilter']) => void;
  setSortBy: (sortBy: FileManagerState['sortBy']) => void;
  setSortOrder: (sortOrder: FileManagerState['sortOrder']) => void;
  setCurrentPage: (page: number) => void;

  fetchFiles: () => Promise<void>;
  createFileFromUpload: (payload: {
    fileName: string;
    fileUrl: string;
    fileKey: string;
    fileSize: number;
    uploadEndpoint: string;
  }) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
  updateFileMetadata: (fileId: string, fileName: string, altText?: string) => Promise<void>;
  markFileUsage: (
    fileId: string,
    isUsed: boolean,
    contentType: string,
    contentId: string,
    fieldName: string,
    options?: { silent?: boolean },
  ) => Promise<void>;
  resolveFileIdsByUrls: (urls: string[]) => Promise<Map<string, string>>;
  cleanupUnusedFiles: (daysOld?: number) => Promise<void>;

  // Pending files management
  addPendingFile: (fileId: string, contentType: string, contentId: string, fieldName: string) => void;
  markPendingFilesAsUsed: (contentType: string, contentId: string, fieldName: string) => Promise<void>;
  /** Marks all pending files for a content record (any fieldName). */
  markPendingFilesForContent: (contentType: string, contentId: string) => Promise<void>;
  clearPendingFiles: (contentType: string, contentId: string, fieldName: string) => void;
  removePendingFile: (fileId: string, contentType: string, contentId: string, fieldName: string) => void;
}

// Combine state and actions
type FileManagerStore = FileManagerState & FileManagerActions;

// 4. Create the Zustand store
const useFileManagerStore = create<FileManagerStore>()((set, get) => {
  // --- Internal fetcher function ---
  const getFiles = async () => {
    const { currentPage, limit, sortBy, sortOrder, search, endpoint, isUsedFilter } = get();
    const params = {
      page: currentPage,
      limit,
      sortBy,
      sortOrder,
      search,
      endpoint: endpoint === 'all' ? undefined : endpoint,
      isUsed: isUsedFilter === 'all' ? undefined : isUsedFilter,
    };

    try {
      const { data } = await SECURE_API.get('/files', { params });
      set({
        files: data.files,
        currentPage: data.currentPage,
        totalPages: data.totalPages,
        totalFiles: data.total,
        loading: false,
      });
    } catch (err: unknown) {
      const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.fetchFiles'));
      enqueueSnackbar(errorMessage, { variant: 'error' });
      set({ error: errorMessage, loading: false, files: [] });
    }
  };

  return {
    // --- State ---
    files: [],
    currentPage: 1,
    totalPages: 1,
    totalFiles: 0,
    loading: false,
    error: null,
    search: '',
    endpoint: 'all',
    isUsedFilter: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    limit: 20,
    pendingFiles: new Map(),

    // --- Actions ---
    setSearch: search => {
      set({ search, currentPage: 1 });
      getFiles(); // Auto-refetch when search changes
    },
    setEndpointFilter: endpoint => {
      set({ endpoint, currentPage: 1 });
      getFiles(); // Auto-refetch when endpoint changes
    },
    setIsUsedFilter: isUsedFilter => {
      set({ isUsedFilter, currentPage: 1 });
      getFiles(); // Auto-refetch when usage filter changes
    },
    setSortBy: sortBy => {
      set({ sortBy, currentPage: 1 });
      getFiles(); // Auto-refetch when sort changes
    },
    setSortOrder: sortOrder => {
      set({ sortOrder, currentPage: 1 });
      getFiles(); // Auto-refetch when sort order changes
    },
    setCurrentPage: page => {
      set({ currentPage: page });
      getFiles(); // Auto-refetch when page changes
    },

    fetchFiles: async () => {
      set({ loading: true });
      return getFiles();
    },

    createFileFromUpload: async payload => {
      try {
        console.log('=== FRONTEND: Creating file from upload ===');
        console.log('Payload being sent:', payload);
        const response = await SECURE_API.post('/files/create', payload);
        console.log('Response received:', response.data);
        // On success, this async function will implicitly return Promise<void>, matching the interface.
      } catch (err: unknown) {
        console.error('=== FRONTEND: Error creating file ===', err);
        const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.createFile'));
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw new Error(errorMessage, { cause: err });
      }
    },

    deleteFile: async fileId => {
      set({ loading: true });
      try {
        await SECURE_API.delete(`/files/${fileId}`);
        enqueueSnackbar(t('system.fileManager.deleteSuccessfulMessage'), { variant: 'success' });
      } catch (err: unknown) {
        const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.deleteFile'));
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        getFiles();
      }
    },

    updateFileMetadata: async (fileId, fileName, altText) => {
      set({ loading: true });
      try {
        await SECURE_API.patch(`/files/${fileId}`, { fileName, altText });
        enqueueSnackbar(t('system.fileManager.updateMetadataSuccess'), { variant: 'success' });
      } catch (err: unknown) {
        const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.updateMetadata'));
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        getFiles();
      }
    },

    markFileUsage: async (fileId, isUsed, contentType, contentId, fieldName, options) => {
      console.log('markFileUsage called:', { fileId, isUsed, contentType, contentId, fieldName });
      set({ loading: true });
      try {
        const response = await SECURE_API.patch(`/files/${fileId}/usage`, {
          isUsed,
          contentType,
          contentId,
          fieldName,
        });
        console.log('File usage updated successfully:', response.data);
        if (!options?.silent) {
          enqueueSnackbar(t('system.fileManager.usageUpdateSuccess'), { variant: 'success' });
        }
      } catch (err: unknown) {
        console.error('Error updating file usage:', err);
        const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.updateUsage'));
        if (!options?.silent) {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      } finally {
        // We refetch even on failure to ensure UI is in sync with backend state
        getFiles();
      }
    },

    resolveFileIdsByUrls: async urls => {
      const uniqueUrls = [...new Set(urls.filter(Boolean))];
      if (uniqueUrls.length === 0) {
        return new Map<string, string>();
      }

      try {
        const { data } = await SECURE_API.post<{ matches: Array<{ url: string; fileId: string }> }>(
          '/files/resolve-urls',
          { urls: uniqueUrls },
        );
        return new Map(data.matches.map(match => [match.url, match.fileId]));
      } catch (err: unknown) {
        console.error('Error resolving file URLs:', err);
        return new Map<string, string>();
      }
    },

    cleanupUnusedFiles: async (daysOld?: number) => {
      set({ loading: true });
      const params = daysOld ? { daysOld } : {};
      try {
        const response = await SECURE_API.delete('/files/cleanup', { params });
        enqueueSnackbar(response.data.message || t('system.fileManager.cleanupSuccess'), {
          variant: 'success',
        });
      } catch (err: unknown) {
        const errorMessage = getAxiosErrorMessage(err, t('system.fileManager.errors.cleanupFiles'));
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        getFiles();
      }
    },

    // Pending files management
    addPendingFile: (fileId, contentType, contentId, fieldName) => {
      const key = `${contentType}:${contentId}:${fieldName}`;
      set(state => {
        const newPendingFiles = new Map(state.pendingFiles);
        const currentFiles = newPendingFiles.get(key) || [];
        if (!currentFiles.includes(fileId)) {
          newPendingFiles.set(key, [...currentFiles, fileId]);
          console.log('Added pending file:', fileId, 'for', key, 'Total pending:', newPendingFiles.get(key)?.length);
        }
        return { pendingFiles: newPendingFiles };
      });
    },

    markPendingFilesAsUsed: async (contentType, contentId, fieldName) => {
      const key = `${contentType}:${contentId}:${fieldName}`;
      const { pendingFiles, markFileUsage } = get();
      const fileIds = pendingFiles.get(key) || [];

      console.log('Marking pending files as used:', fileIds, 'for', key);

      if (fileIds.length === 0) {
        console.log('No pending files to mark as used');
        return;
      }

      try {
        // Check if user is authenticated before making the API call
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.warn('User not authenticated, skipping file usage tracking');
          return;
        }

        for (const fileId of fileIds) {
          console.log('Marking file as used:', fileId);
          await markFileUsage(fileId, true, contentType, contentId, fieldName);
        }

        // Clear the pending files after successful marking
        set(state => {
          const newPendingFiles = new Map(state.pendingFiles);
          newPendingFiles.delete(key);
          return { pendingFiles: newPendingFiles };
        });

        console.log('All pending files marked as used and cleared');
      } catch (error) {
        console.error('Error marking pending files as used:', error);
        // Don't throw the error to prevent breaking the main functionality
      }
    },

    markPendingFilesForContent: async (contentType, contentId) => {
      const prefix = `${contentType}:${contentId}:`;
      const { pendingFiles, markPendingFilesAsUsed } = get();
      const fieldNames = new Set<string>();
      for (const key of pendingFiles.keys()) {
        if (key.startsWith(prefix)) {
          fieldNames.add(key.slice(prefix.length));
        }
      }
      for (const fieldName of fieldNames) {
        await markPendingFilesAsUsed(contentType, contentId, fieldName);
      }
    },

    clearPendingFiles: (contentType, contentId, fieldName) => {
      const key = `${contentType}:${contentId}:${fieldName}`;
      set(state => {
        const newPendingFiles = new Map(state.pendingFiles);
        newPendingFiles.delete(key);
        console.log('Cleared pending files for', key);
        return { pendingFiles: newPendingFiles };
      });
    },

    removePendingFile: (fileId, contentType, contentId, fieldName) => {
      const key = `${contentType}:${contentId}:${fieldName}`;
      set(state => {
        const newPendingFiles = new Map(state.pendingFiles);
        const currentFiles = newPendingFiles.get(key) || [];
        const updatedFiles = currentFiles.filter(id => id !== fileId);
        if (updatedFiles.length === 0) {
          newPendingFiles.delete(key);
        } else {
          newPendingFiles.set(key, updatedFiles);
        }
        return { pendingFiles: newPendingFiles };
      });
    },
  };
});

export default useFileManagerStore;
